import { ReactElement, ReactNode } from 'react'
import { IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { alpha } from '@mui/material/styles'

interface OptionEndAction {
  Icon: ReactNode
  tooltip: string
  onClick: () => void
  dataCy?: string
}

interface Option {
  label: string
  value: string
  Icon?: ReactNode
  disabled?: boolean
  /** Overrides the default `load-option-<value>` test selector. */
  dataCy?: string
  /** Secondary icon button at the right of the row; clicking it does not select the option. */
  endAction?: OptionEndAction
}

interface Props {
  value: any // eslint-disable-line
  onClick: (value: any) => void // eslint-disable-line
  options: Option[]
}

const ToggleDropDownOptions = ({ onClick, options, value: currentValue }: Props): ReactElement => {
  return (
    <Stack direction="column" sx={{ minWidth: 220 }}>
      {options.map(({ value, label, Icon, disabled, dataCy, endAction }) => {
        const isSelected = value === currentValue
        return (
          <Stack
            key={value}
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            data-cy={dataCy ?? `load-option-${value}`}
            onClick={event => {
              event.stopPropagation()
              if (disabled) return
              onClick(value)
            }}
            sx={theme => ({
              px: 1.5,
              py: endAction ? 0.25 : 0.75,
              cursor: disabled ? 'not-allowed' : 'pointer',
              userSelect: 'none',
              opacity: disabled ? 0.4 : 1,
              color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
              backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
              transition: theme.transitions.create(['background-color', 'color'], {
                duration: theme.transitions.duration.shortest,
              }),
              '&:hover': {
                backgroundColor: isSelected
                  ? alpha(theme.palette.primary.main, 0.12)
                  : alpha(theme.palette.common.black, 0.04),
              },
            })}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSelected ? 500 : 400,
                whiteSpace: 'nowrap',
              }}
            >
              {label}
            </Typography>
            {(Icon || endAction) && (
              <Stack direction="row" alignItems="center" sx={{ ml: 1 }}>
                {Icon}
                {endAction && (
                  <Tooltip title={endAction.tooltip} placement="right">
                    <span>
                      <IconButton
                        size="small"
                        disabled={disabled}
                        data-cy={endAction.dataCy}
                        onClick={event => {
                          event.stopPropagation()
                          endAction.onClick()
                        }}
                      >
                        {endAction.Icon}
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Stack>
            )}
          </Stack>
        )
      })}
    </Stack>
  )
}

export default ToggleDropDownOptions
